/**
 * Shared health data type definitions used by both HealthIntegrationScreen
 * and the background sync utility (healthBackground.ts).
 */

export type HealthTypeConfig = {
  key: string;
  label: string;
  icon: [string, string];
  iconColor: string;
  // iOS HealthKit
  hkIdentifier: string;
  isCategoryType?: boolean;
  /**
   * For category types: compute the sample value as duration in minutes
   * (endDate - startDate) rather than using the raw category integer.
   * Used for SleepAnalysis and MindfulSession.
   */
  isDurationBased?: boolean;
  /**
   * For SleepAnalysis specifically: when isDurationBased is true, only
   * accumulate samples whose category value represents an asleep stage
   * (1=asleep legacy, 3=core/light, 4=deep, 5=REM) — excludes inBed and awake.
   */
  sleepAsleepOnly?: boolean;
  /** True for types that accumulate across a day (sum), false/undefined for instantaneous (average). */
  cumulative?: boolean;
  // Android Health Connect
  hcRecordType?: string;
  hcValueExtractor?: (record: any) => { value: number; unit: string } | null;
};

export type HealthCategory = {
  id: string;
  label: string;
  icon: [string, string];
  iconColor: string;
  types: HealthTypeConfig[];
};

export const HEALTH_CATEGORIES: HealthCategory[] = [
  {
    id: 'activity',
    label: 'Activity',
    icon: ['fad', 'person-running'],
    iconColor: '#10B981',
    types: [
      {
        key: 'StepCount', label: 'Steps',
        icon: ['fad', 'shoe-prints'], iconColor: '#10B981',
        hkIdentifier: 'HKQuantityTypeIdentifierStepCount',
        cumulative: true,
        hcRecordType: 'Steps',
        hcValueExtractor: (r) => r.count != null ? { value: r.count, unit: 'steps' } : null,
      },
      {
        key: 'ActiveEnergyBurned', label: 'Active Calories',
        icon: ['fad', 'fire-flame-curved'], iconColor: '#F97316',
        hkIdentifier: 'HKQuantityTypeIdentifierActiveEnergyBurned',
        cumulative: true,
        hcRecordType: 'ActiveCaloriesBurned',
        hcValueExtractor: (r) => r.energy?.inKilocalories != null ? { value: r.energy.inKilocalories, unit: 'kcal' } : null,
      },
      {
        key: 'ExerciseTime', label: 'Exercise Time',
        icon: ['fad', 'stopwatch'], iconColor: '#10B981',
        hkIdentifier: 'HKQuantityTypeIdentifierAppleExerciseTime',
        cumulative: true,
        hcRecordType: 'ExerciseSession',
        hcValueExtractor: (r) => {
          if (!r.startTime || !r.endTime) return null;
          const start = new Date(r.startTime).getTime();
          const end = new Date(r.endTime).getTime();
          if (isNaN(start) || isNaN(end) || end <= start) return null;
          return { value: Math.round((end - start) / 60000), unit: 'min' };
        },
      },
      {
        key: 'FlightsClimbed', label: 'Flights Climbed',
        icon: ['fad', 'stairs'], iconColor: '#10B981',
        hkIdentifier: 'HKQuantityTypeIdentifierFlightsClimbed',
        cumulative: true,
        hcRecordType: 'FloorsClimbed',
        hcValueExtractor: (r) => r.floors != null ? { value: r.floors, unit: 'floors' } : null,
      },
    ],
  },
  {
    id: 'vitals',
    label: 'Vitals',
    icon: ['fad', 'heart-pulse'],
    iconColor: '#EF4444',
    types: [
      {
        key: 'HeartRate', label: 'Heart Rate',
        icon: ['fad', 'heart-pulse'], iconColor: '#EF4444',
        hkIdentifier: 'HKQuantityTypeIdentifierHeartRate',
        hcRecordType: 'HeartRate',
        hcValueExtractor: (r) => {
          const s = r.samples?.[0];
          return s?.beatsPerMinute != null ? { value: s.beatsPerMinute, unit: 'bpm' } : null;
        },
      },
      {
        key: 'RestingHeartRate', label: 'Resting Heart Rate',
        icon: ['fad', 'heart'], iconColor: '#EF4444',
        hkIdentifier: 'HKQuantityTypeIdentifierRestingHeartRate',
        hcRecordType: 'RestingHeartRate',
        hcValueExtractor: (r) => r.beatsPerMinute != null ? { value: r.beatsPerMinute, unit: 'bpm' } : null,
      },
      {
        key: 'HeartRateVariability', label: 'Heart Rate Variability',
        icon: ['fad', 'heart-pulse'], iconColor: '#EC4899',
        hkIdentifier: 'HKQuantityTypeIdentifierHeartRateVariabilitySDNN',
      },
      {
        key: 'WalkingHeartRateAverage', label: 'Walking Heart Rate',
        icon: ['fad', 'heart'], iconColor: '#F87171',
        hkIdentifier: 'HKQuantityTypeIdentifierWalkingHeartRateAverage',
      },
      {
        key: 'HeartRateRecovery', label: 'HR Recovery (1-min)',
        icon: ['fad', 'heart'], iconColor: '#F43F5E',
        hkIdentifier: 'HKQuantityTypeIdentifierHeartRateRecoveryOneMinute',
      },
      {
        key: 'AtrialFibrillationBurden', label: 'AFib Burden',
        icon: ['fad', 'heart'], iconColor: '#DC2626',
        hkIdentifier: 'HKQuantityTypeIdentifierAtrialFibrillationBurden',
      },
      {
        key: 'BloodPressureSystolic', label: 'Blood Pressure (Systolic)',
        icon: ['fad', 'droplet'], iconColor: '#EF4444',
        hkIdentifier: 'HKQuantityTypeIdentifierBloodPressureSystolic',
        hcRecordType: 'BloodPressure',
        hcValueExtractor: (r) => r.systolic?.inMillimetersOfMercury != null ? { value: r.systolic.inMillimetersOfMercury, unit: 'mmHg' } : null,
      },
      {
        key: 'BloodPressureDiastolic', label: 'Blood Pressure (Diastolic)',
        icon: ['fad', 'droplet'], iconColor: '#F87171',
        hkIdentifier: 'HKQuantityTypeIdentifierBloodPressureDiastolic',
        hcRecordType: 'BloodPressure',
        hcValueExtractor: (r) => r.diastolic?.inMillimetersOfMercury != null ? { value: r.diastolic.inMillimetersOfMercury, unit: 'mmHg' } : null,
      },
      {
        key: 'BloodGlucose', label: 'Blood Glucose',
        icon: ['fad', 'droplet'], iconColor: '#F97316',
        hkIdentifier: 'HKQuantityTypeIdentifierBloodGlucose',
        hcRecordType: 'BloodGlucose',
        hcValueExtractor: (r) => {
          const mgdl = r.level?.inMilligramsPerDeciliter;
          const mmol = r.level?.inMillimolesPerLiter;
          const val = mgdl ?? mmol;
          return val != null ? { value: Math.round(val * 10) / 10, unit: mgdl != null ? 'mg/dL' : 'mmol/L' } : null;
        },
      },
      {
        key: 'OxygenSaturation', label: 'Blood Oxygen',
        icon: ['fad', 'lungs'], iconColor: '#60A5FA',
        hkIdentifier: 'HKQuantityTypeIdentifierOxygenSaturation',
        hcRecordType: 'OxygenSaturation',
        hcValueExtractor: (r) => r.percentage != null ? { value: Math.round(r.percentage * 100), unit: '%' } : null,
      },
      {
        key: 'BodyTemperature', label: 'Body Temperature',
        icon: ['fad', 'temperature-half'], iconColor: '#F59E0B',
        hkIdentifier: 'HKQuantityTypeIdentifierBodyTemperature',
        hcRecordType: 'BodyTemperature',
        hcValueExtractor: (r) => r.temperature?.inCelsius != null ? { value: r.temperature.inCelsius, unit: '°C' } : null,
      },
      {
        key: 'RespiratoryRate', label: 'Respiratory Rate',
        icon: ['fad', 'lungs'], iconColor: '#818CF8',
        hkIdentifier: 'HKQuantityTypeIdentifierRespiratoryRate',
        hcRecordType: 'RespiratoryRate',
        hcValueExtractor: (r) => r.rate != null ? { value: r.rate, unit: 'breaths/min' } : null,
      },
    ],
  },
  {
    id: 'body',
    label: 'Body',
    icon: ['fad', 'weight-scale'],
    iconColor: '#8B5CF6',
    types: [
      {
        key: 'BodyMass', label: 'Weight',
        icon: ['fad', 'weight-scale'], iconColor: '#8B5CF6',
        hkIdentifier: 'HKQuantityTypeIdentifierBodyMass',
        hcRecordType: 'Weight',
        hcValueExtractor: (r) => r.weight?.inKilograms != null ? { value: r.weight.inKilograms, unit: 'kg' } : null,
      },
      {
        key: 'BMI', label: 'BMI',
        icon: ['fad', 'chart-simple'], iconColor: '#8B5CF6',
        hkIdentifier: 'HKQuantityTypeIdentifierBodyMassIndex',
      },
      {
        key: 'Height', label: 'Height',
        icon: ['fad', 'ruler-vertical'], iconColor: '#8B5CF6',
        hkIdentifier: 'HKQuantityTypeIdentifierHeight',
        hcRecordType: 'Height',
        hcValueExtractor: (r) => r.height?.inMeters != null ? { value: r.height.inMeters, unit: 'm' } : null,
      },
      {
        key: 'BodyFatPercentage', label: 'Body Fat %',
        icon: ['fad', 'person'], iconColor: '#8B5CF6',
        hkIdentifier: 'HKQuantityTypeIdentifierBodyFatPercentage',
        hcRecordType: 'BodyFat',
        hcValueExtractor: (r) => r.percentage != null ? { value: Math.round(r.percentage * 100), unit: '%' } : null,
      },
      {
        key: 'LeanBodyMass', label: 'Lean Body Mass',
        icon: ['fad', 'dumbbell'], iconColor: '#8B5CF6',
        hkIdentifier: 'HKQuantityTypeIdentifierLeanBodyMass',
        hcRecordType: 'LeanBodyMass',
        hcValueExtractor: (r) => r.mass?.inKilograms != null ? { value: r.mass.inKilograms, unit: 'kg' } : null,
      },
      {
        key: 'WaistCircumference', label: 'Waist Circumference',
        icon: ['fad', 'ruler-vertical'], iconColor: '#7C3AED',
        hkIdentifier: 'HKQuantityTypeIdentifierWaistCircumference',
      },
      {
        key: 'BasalEnergyBurned', label: 'Resting Calories',
        icon: ['fad', 'fire-flame-curved'], iconColor: '#FB923C',
        hkIdentifier: 'HKQuantityTypeIdentifierBasalEnergyBurned',
        cumulative: true,
        hcRecordType: 'TotalCaloriesBurned',
        hcValueExtractor: (r) => r.energy?.inKilocalories != null ? { value: Math.round(r.energy.inKilocalories), unit: 'kcal' } : null,
      },
      {
        key: 'SleepingWristTemperature', label: 'Sleeping Wrist Temp',
        icon: ['fad', 'temperature-half'], iconColor: '#6366F1',
        hkIdentifier: 'HKQuantityTypeIdentifierAppleSleepingWristTemperature',
      },
    ],
  },
  {
    id: 'sleep',
    label: 'Sleep',
    icon: ['fad', 'moon'],
    iconColor: '#6366F1',
    types: [
      {
        key: 'SleepAnalysis', label: 'Sleep (Total)',
        icon: ['fad', 'moon'], iconColor: '#6366F1',
        hkIdentifier: 'HKCategoryTypeIdentifierSleepAnalysis',
        isCategoryType: true,
        isDurationBased: true,
        sleepAsleepOnly: true,
        cumulative: true,
        hcRecordType: 'SleepSession',
        hcValueExtractor: (r) => {
          if (!r.startTime || !r.endTime) return null;
          const start = new Date(r.startTime).getTime();
          const end = new Date(r.endTime).getTime();
          if (isNaN(start) || isNaN(end) || end <= start) return null;
          return { value: Math.round((end - start) / 60000), unit: 'min' };
        },
      },
    ],
  },
  {
    id: 'mobility',
    label: 'Mobility',
    icon: ['fad', 'person-walking'],
    iconColor: '#0284C7',
    types: [
      {
        key: 'VO2Max', label: 'VO₂ Max',
        icon: ['fad', 'person-running'], iconColor: '#0284C7',
        hkIdentifier: 'HKQuantityTypeIdentifierVO2Max',
        hcRecordType: 'Vo2Max',
        hcValueExtractor: (r) => r.vo2MillilitersPerMinuteKilogram != null
          ? { value: Math.round(r.vo2MillilitersPerMinuteKilogram * 10) / 10, unit: 'mL/kg/min' }
          : null,
      },
      {
        key: 'WalkingSteadiness', label: 'Walking Steadiness',
        icon: ['fad', 'person-walking'], iconColor: '#0284C7',
        hkIdentifier: 'HKQuantityTypeIdentifierAppleWalkingSteadiness',
      },
      {
        key: 'NumberOfTimesFallen', label: 'Falls Detected',
        icon: ['fad', 'person'], iconColor: '#EF4444',
        hkIdentifier: 'HKQuantityTypeIdentifierNumberOfTimesFallen',
        cumulative: true,
      },
      {
        key: 'WalkingSpeed', label: 'Walking Speed',
        icon: ['fad', 'gauge'], iconColor: '#0284C7',
        hkIdentifier: 'HKQuantityTypeIdentifierWalkingSpeed',
      },
      {
        key: 'WalkingStepLength', label: 'Step Length',
        icon: ['fad', 'shoe-prints'], iconColor: '#0369A1',
        hkIdentifier: 'HKQuantityTypeIdentifierWalkingStepLength',
      },
      {
        key: 'WalkingAsymmetry', label: 'Walking Asymmetry',
        icon: ['fad', 'person-walking'], iconColor: '#0369A1',
        hkIdentifier: 'HKQuantityTypeIdentifierWalkingAsymmetryPercentage',
      },
      {
        key: 'SixMinuteWalkDistance', label: '6-Min Walk Distance',
        icon: ['fad', 'ruler-vertical'], iconColor: '#0369A1',
        hkIdentifier: 'HKQuantityTypeIdentifierSixMinuteWalkTestDistance',
      },
    ],
  },
  {
    id: 'clinical',
    label: 'Clinical',
    icon: ['fad', 'syringe'],
    iconColor: '#0EA5E9',
    types: [
      {
        key: 'InsulinDelivery', label: 'Insulin Delivery',
        icon: ['fad', 'syringe'], iconColor: '#0EA5E9',
        hkIdentifier: 'HKQuantityTypeIdentifierInsulinDelivery',
        cumulative: true,
      },
      {
        key: 'InhalerUsage', label: 'Inhaler Usage',
        icon: ['fad', 'lungs'], iconColor: '#7C3AED',
        hkIdentifier: 'HKQuantityTypeIdentifierInhalerUsage',
        cumulative: true,
      },
      {
        key: 'NumberOfAlcoholicBeverages', label: 'Alcoholic Beverages',
        icon: ['fad', 'glass-water'], iconColor: '#9333EA',
        hkIdentifier: 'HKQuantityTypeIdentifierNumberOfAlcoholicBeverages',
        cumulative: true,
      },
    ],
  },
  {
    id: 'mind',
    label: 'Mind & Environment',
    icon: ['fad', 'brain'],
    iconColor: '#7C3AED',
    types: [
      {
        key: 'MindfulSession', label: 'Mindful Minutes',
        icon: ['fad', 'brain'], iconColor: '#7C3AED',
        hkIdentifier: 'HKCategoryTypeIdentifierMindfulSession',
        isCategoryType: true,
        isDurationBased: true,
        cumulative: true,
      },
      {
        key: 'TimeInDaylight', label: 'Time in Daylight',
        icon: ['fad', 'sun'], iconColor: '#F59E0B',
        hkIdentifier: 'HKQuantityTypeIdentifierTimeInDaylight',
        cumulative: true,
      },
    ],
  },
  {
    id: 'nutrition',
    label: 'Nutrition',
    icon: ['fad', 'apple-whole'],
    iconColor: '#F59E0B',
    types: [
      {
        key: 'DietaryEnergyConsumed', label: 'Calories Consumed',
        icon: ['fad', 'utensils'], iconColor: '#F59E0B',
        hkIdentifier: 'HKQuantityTypeIdentifierDietaryEnergyConsumed',
        cumulative: true,
        hcRecordType: 'Nutrition',
        hcValueExtractor: (r) => r.energy?.inKilocalories != null ? { value: r.energy.inKilocalories, unit: 'kcal' } : null,
      },
      {
        key: 'DietaryProtein', label: 'Protein',
        icon: ['fad', 'drumstick-bite'], iconColor: '#F59E0B',
        hkIdentifier: 'HKQuantityTypeIdentifierDietaryProtein',
        cumulative: true,
        hcRecordType: 'Nutrition',
        hcValueExtractor: (r) => r.protein?.inGrams != null ? { value: r.protein.inGrams, unit: 'g' } : null,
      },
      {
        key: 'DietaryCarbohydrates', label: 'Carbohydrates',
        icon: ['fad', 'bread-slice'], iconColor: '#F59E0B',
        hkIdentifier: 'HKQuantityTypeIdentifierDietaryCarbohydrates',
        cumulative: true,
        hcRecordType: 'Nutrition',
        hcValueExtractor: (r) => r.totalCarbohydrate?.inGrams != null ? { value: r.totalCarbohydrate.inGrams, unit: 'g' } : null,
      },
      {
        key: 'DietaryFatTotal', label: 'Total Fat',
        icon: ['fad', 'droplet'], iconColor: '#FBBF24',
        hkIdentifier: 'HKQuantityTypeIdentifierDietaryFatTotal',
        cumulative: true,
        hcRecordType: 'Nutrition',
        hcValueExtractor: (r) => r.totalFat?.inGrams != null ? { value: r.totalFat.inGrams, unit: 'g' } : null,
      },
      {
        key: 'DietaryWater', label: 'Water',
        icon: ['fad', 'glass-water'], iconColor: '#60A5FA',
        hkIdentifier: 'HKQuantityTypeIdentifierDietaryWater',
        cumulative: true,
        hcRecordType: 'Hydration',
        hcValueExtractor: (r) => r.volume?.inLiters != null ? { value: r.volume.inLiters * 1000, unit: 'mL' } : null,
      },
    ],
  },
];

export const ALL_HEALTH_TYPE_KEYS = HEALTH_CATEGORIES.flatMap(c => c.types.map(t => t.key));
